import { useMemo, useState } from "react";
import { ChevronDown, ChevronUp, X, SlidersHorizontal } from "lucide-react";
import { categories, allTools } from "@/data/categories";
import { slugify } from "@/pages/ToolDetail";
import type { LibraryEntry } from "@/hooks/useLibrary";

const PRICING_OPTIONS = ["Free", "Freemium", "Paid", "Open Source"] as const;

export interface LibraryFilterState {
  /** Tool category (matches `AITool.category`). Filters the shortlist only. */
  category: string | null;
  /** Tool pricing tier. Filters the shortlist only. */
  pricing: string | null;
  /** Free-form tags. Filters BOTH shortlist (tool tags) and entries (entry tags). */
  tags: string[];
}

interface LibraryFiltersPanelProps {
  state: LibraryFilterState;
  onChange: (next: LibraryFilterState) => void;
  /** Bookmarked tool slugs — used to populate the tag dropdown with relevant options. */
  bookmarkedSlugs: string[];
  /** Community library entries — used to populate the tag dropdown with relevant options. */
  entries: LibraryEntry[];
}

/**
 * Combined filters panel for the Library page. Drives both the personal
 * shortlist (categories + pricing + tags) and the community entries (tags).
 * Collapsed by default to keep the page calm — opens with a chip-led summary
 * of any active filters so the user can spot what's narrowing their view.
 */
const LibraryFiltersPanel = ({
  state,
  onChange,
  bookmarkedSlugs,
  entries,
}: LibraryFiltersPanelProps) => {
  const [open, setOpen] = useState(false);

  // Tag universe: union of tags on bookmarked tools and community entries.
  // Sorted by frequency so the most-used tags float to the top.
  const tagOptions = useMemo(() => {
    const counts = new Map<string, number>();
    const bump = (raw?: string | null) => {
      const t = (raw ?? "").trim();
      if (!t) return;
      counts.set(t, (counts.get(t) ?? 0) + 1);
    };
    const slugSet = new Set(bookmarkedSlugs);
    for (const tool of allTools) {
      if (!slugSet.has(slugify(tool.name))) continue;
      (tool.tags ?? []).forEach(bump);
    }
    for (const entry of entries) {
      (entry.tags ?? []).forEach(bump);
    }
    return Array.from(counts.entries())
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
      .slice(0, 24)
      .map(([t]) => t);
  }, [bookmarkedSlugs, entries]);

  const activeCount =
    (state.category ? 1 : 0) + (state.pricing ? 1 : 0) + state.tags.length;

  const set = (patch: Partial<LibraryFilterState>) =>
    onChange({ ...state, ...patch });

  const toggleTag = (tag: string) => {
    set({
      tags: state.tags.includes(tag)
        ? state.tags.filter((t) => t !== tag)
        : [...state.tags, tag],
    });
  };

  return (
    <div className="mb-6 rounded-lg border border-border bg-card/40">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 font-mono text-sm hover:bg-card/60 transition-colors rounded-lg"
        aria-expanded={open}
      >
        <span className="flex items-center gap-2">
          <SlidersHorizontal className="w-4 h-4 text-primary" />
          <span className="font-semibold">Filters</span>
          {activeCount > 0 && (
            <span className="ml-1 inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full bg-primary/15 text-primary text-[11px]">
              {activeCount}
            </span>
          )}
        </span>
        {open ? (
          <ChevronUp className="w-4 h-4 text-muted-foreground" />
        ) : (
          <ChevronDown className="w-4 h-4 text-muted-foreground" />
        )}
      </button>

      {/* Inline summary chips when collapsed but active */}
      {!open && activeCount > 0 && (
        <div className="px-4 pb-3 flex flex-wrap items-center gap-1.5">
          {state.category && (
            <SummaryChip label={state.category} onClear={() => set({ category: null })} />
          )}
          {state.pricing && (
            <SummaryChip label={state.pricing} onClear={() => set({ pricing: null })} />
          )}
          {state.tags.map((t) => (
            <SummaryChip key={t} label={`#${t}`} onClear={() => toggleTag(t)} />
          ))}
          <button
            type="button"
            onClick={() => onChange({ category: null, pricing: null, tags: [] })}
            className="text-[11px] font-mono text-muted-foreground hover:text-foreground ml-1"
          >
            Clear all
          </button>
        </div>
      )}

      {open && (
        <div className="px-4 pb-4 pt-1 border-t border-border space-y-4">
          {/* Category */}
          <Section title="Category" hint="Narrows your bookmarked tools">
            <div className="flex flex-wrap gap-1.5">
              <Chip
                active={!state.category}
                onClick={() => set({ category: null })}
              >
                All
              </Chip>
              {categories.slice(0, 14).map((c) => (
                <Chip
                  key={c.name}
                  active={state.category === c.name}
                  onClick={() =>
                    set({ category: state.category === c.name ? null : c.name })
                  }
                >
                  <span className="mr-1">{c.icon}</span>
                  {c.name}
                </Chip>
              ))}
            </div>
          </Section>

          {/* Pricing */}
          <Section title="Pricing" hint="Narrows your bookmarked tools">
            <div className="flex flex-wrap gap-1.5">
              <Chip active={!state.pricing} onClick={() => set({ pricing: null })}>
                All
              </Chip>
              {PRICING_OPTIONS.map((p) => (
                <Chip
                  key={p}
                  active={state.pricing === p}
                  onClick={() =>
                    set({ pricing: state.pricing === p ? null : p })
                  }
                >
                  {p}
                </Chip>
              ))}
            </div>
          </Section>

          {/* Tags */}
          <Section
            title="Tags"
            hint="Applies to both bookmarks and community entries"
          >
            {tagOptions.length === 0 ? (
              <p className="text-xs font-mono text-muted-foreground">
                No tags yet — bookmark a few tools or browse some entries to
                populate this list.
              </p>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {tagOptions.map((t) => {
                  const active = state.tags.includes(t);
                  return (
                    <Chip key={t} active={active} onClick={() => toggleTag(t)}>
                      #{t}
                    </Chip>
                  );
                })}
              </div>
            )}
          </Section>

          {activeCount > 0 && (
            <div className="pt-1">
              <button
                type="button"
                onClick={() => onChange({ category: null, pricing: null, tags: [] })}
                className="text-xs font-mono text-muted-foreground hover:text-foreground"
              >
                Reset all filters
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// --- internal -------------------------------------------------------------
const Section = ({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: string;
  children: React.ReactNode;
}) => (
  <div>
    <div className="flex items-baseline justify-between mb-1.5">
      <h3 className="text-[11px] font-mono font-semibold uppercase tracking-wider text-muted-foreground">
        {title}
      </h3>
      {hint && <span className="text-[10px] font-mono text-muted-foreground">{hint}</span>}
    </div>
    {children}
  </div>
);

const Chip = ({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={active}
    className={`text-xs font-mono px-2.5 py-1 rounded-md border transition-colors ${
      active
        ? "bg-primary text-primary-foreground border-primary font-semibold"
        : "bg-secondary border-border text-muted-foreground hover:text-foreground hover:border-primary/30"
    }`}
  >
    {children}
  </button>
);

const SummaryChip = ({ label, onClear }: { label: string; onClear: () => void }) => (
  <button
    type="button"
    onClick={onClear}
    className="group inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-primary/10 border border-primary/40 text-primary text-[11px] font-mono hover:bg-primary/20 transition-colors"
  >
    {label}
    <X className="w-3 h-3 group-hover:scale-110 transition-transform" />
  </button>
);

export default LibraryFiltersPanel;